// src/pages/certs/cert-list/index.jsx
import { useFormik } from "formik";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";
import toastr from "toastr";
import * as Yup from "yup";
import {
  useCreateCert,
  useGetAdminCourses,
  useGetAllCerts,
  useToggleCertStatus,
  useUpdateCert,
} from "../../../api/certs";
import { useDebounce } from "../../../hooks/useDebounce";
import { resolvePaginatedList } from "../../../helpers/paginatedResponse";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import FormSelectField from "../../../components/Common/FormSelectField";
import Paginations from "../../../components/Common/Pagination";
import Spinners from "../../../components/Common/Spinner";
import TableContainer from "../../../components/Common/TableContainer";

function slugToSingleSelectValue(slug, optionList) {
  const s = slug != null ? String(slug).trim() : "";
  if (!s) return null;
  const match = optionList.find((o) => o.value === s);
  return match || { value: s, label: s };
}

/** Legacy multi-course / embedded `courses` (older API). */
function getCertCourseSlugs(cert) {
  if (!cert) return [];
  if (Array.isArray(cert.courses) && cert.courses.length > 0) {
    return cert.courses.map((c) => c?.slug).filter(Boolean);
  }
  if (Array.isArray(cert.courseSlugs)) {
    return cert.courseSlugs.map(String);
  }
  return [];
}

/** Single `courseSlug` for forms: API field, nested `course`, `courseId` + loaded courses, or legacy. */
function resolveCertCourseSlug(cert, options) {
  if (!cert) return "";
  if (cert.courseSlug != null && String(cert.courseSlug).trim() !== "") {
    return String(cert.courseSlug).trim();
  }
  if (cert.course?.slug != null && String(cert.course.slug).trim() !== "") {
    return String(cert.course.slug).trim();
  }
  if (cert.courseId && Array.isArray(options) && options.length > 0) {
    const match = options.find((c) => c.id === cert.courseId);
    return match?.slug ? String(match.slug) : "";
  }
  const legacy = getCertCourseSlugs(cert);
  return legacy[0] ? String(legacy[0]) : "";
}

/* Legacy: certificate `type` (gold/silver) removed from API — keep for reference if backend restores it.
const TYPE_OPTIONS = [
  { value: "gold", label: "Gold" },
  { value: "silver", label: "Silver" }
];
*/

const BOOL_FILTER_OPTIONS = [
  { value: "all", label: "All" },
  { value: "true", label: "Yes" },
  { value: "false", label: "No" },
];

const CertList = () => {
  document.title = "Certificates List | Admin";

  // server-side controls
  const [search, setSearch] = useState(""); // server search (e.g. title)
  const debouncedSearch = useDebounce(search, 400);
  const [page, setPage] = useState(1); // 1-based
  const [limit, setLimit] = useState(10);
  const [isActiveFilter, setIsActiveFilter] = useState("all");
  const [showDBSFilter, setShowDBSFilter] = useState("all");
  const [isComplianceFilter, setIsComplianceFilter] = useState("all");

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [selectedCert, setSelectedCert] = useState(null);
  const [pendingToggleSlug, setPendingToggleSlug] = useState(null);
  const [certsState, setCertsState] = useState({ list: [], total: 0 });

  // mutation hooks
  const { mutate, data, isLoading, isPending, error } = useGetAllCerts();
  const { mutate: fetchAdminCourses, data: adminCoursesData } =
    useGetAdminCourses();
  const createCertMutation = useCreateCert();
  const updateCertMutation = useUpdateCert();
  const toggleCertStatus = useToggleCertStatus();
  const { mutate: toggleMutate, isPending: togglePending } = toggleCertStatus;
  const loading = isLoading || isPending;

  const buildListParams = useCallback(() => {
    const payload = { page, limit };
    if (isActiveFilter !== "all") {
      payload.isActive = isActiveFilter;
    }
    if (showDBSFilter !== "all") {
      payload.showDBS = showDBSFilter;
    }
    if (isComplianceFilter !== "all") {
      payload.isComplianceCertificate = isComplianceFilter;
    }
    const trimmed = debouncedSearch.trim();
    if (trimmed) {
      payload.search = trimmed;
    }
    return payload;
  }, [
    page,
    limit,
    isActiveFilter,
    showDBSFilter,
    isComplianceFilter,
    debouncedSearch,
  ]);

  // Build a stable fetch key and guard against duplicate calls (e.g., StrictMode double invoke)
  const lastFetchKeyRef = useRef("");
  const fetchKey = JSON.stringify({
    page,
    limit,
    isActiveFilter,
    showDBSFilter,
    isComplianceFilter,
    search: debouncedSearch,
  });

  // fire fetch whenever fetchKey changes
  useEffect(() => {
    if (lastFetchKeyRef.current === fetchKey) return;
    lastFetchKeyRef.current = fetchKey;
    mutate(buildListParams());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchKey]);

  const certs = certsState.list;
  const total = certsState.total;

  useEffect(() => {
    const { list, total: nextTotal } = resolvePaginatedList(data, {
      listKeys: ["data", "items", "certs", "certificates"],
    });
    setCertsState({ list, total: nextTotal });
  }, [data]);

  const courseOptions = useMemo(() => {
    const raw = adminCoursesData?.data;
    const arr = Array.isArray(raw)
      ? raw
      : Array.isArray(adminCoursesData?.data?.items)
      ? adminCoursesData.data.items
      : [];
    const fromApi = arr
      .map((c) => ({
        id: c.id ?? "",
        slug: c.slug ?? c.courseSlug ?? "",
        title: c.title ?? c.name ?? c.slug ?? "Course",
      }))
      .filter((c) => c.slug);

    // Unassigned-courses API omits the cert's current course; merge it in for edit modal options.
    const merged = [...fromApi];
    if (showUpdateModal && selectedCert?.course?.slug) {
      const ec = selectedCert.course;
      const slug = String(ec.slug).trim();
      if (slug && !merged.some((c) => c.slug === slug)) {
        merged.unshift({
          id: ec.id ?? selectedCert.courseId ?? "",
          slug,
          title: ec.name ?? ec.title ?? ec.slug ?? "Course",
        });
      }
    }
    return merged;
  }, [adminCoursesData, showUpdateModal, selectedCert]);

  const courseSelectOptions = useMemo(
    () => courseOptions.map((c) => ({ value: c.slug, label: c.title })),
    [courseOptions]
  );

  const courseIdToLabel = useMemo(() => {
    const m = new Map();
    for (const c of courseOptions) {
      if (c.id) m.set(c.id, c.title);
    }
    return m;
  }, [courseOptions]);

  useEffect(() => {
    if (showCreateModal || showUpdateModal) {
      fetchAdminCourses({
        limit: null,
        showCourseLinkToCert: false,
      });
    }
  }, [showCreateModal, showUpdateModal, fetchAdminCourses]);

  // Formik for create form
  const createFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      title: "",
      description: "",
      icon: "",
      points: 1,
      courseSlug: "",
      isComplianceCertificate: false,
    },
    validationSchema: Yup.object({
      title: Yup.string().trim().required("Title is required"),
      description: Yup.string().trim(),
      icon: Yup.string()
        .trim()
        .max(4, "Icon must be maximum 4 characters")
        .required("Icon is required"),
      points: Yup.number()
        .typeError("Points must be a number")
        .integer("Points must be a whole number")
        .min(0, "Points cannot be negative")
        .required("Points is required"),
      courseSlug: Yup.string().trim(),
    }),
    onSubmit: (values) => {
      const payload = {
        title: values.title.trim(),
        hasServiceNumber: false, // Always send false
        icon: values.icon.trim(),
        points: Number(values.points),
        isComplianceCertificate: !!values.isComplianceCertificate,
      };

      if (values.description.trim()) {
        payload.description = values.description.trim();
      }

      const cs = values.courseSlug?.trim();
      if (cs) {
        payload.courseSlug = cs;
      }

      createCertMutation.mutate(payload, {
        onSuccess: () => {
          toastr.success("Certificate created successfully!");
          setShowCreateModal(false);
          createFormik.resetForm();
          // Refresh the list
          mutate(buildListParams());
        },
        onError: () => {
          // Error toast is handled by global axios interceptor
        },
      });
    },
  });

  // Formik for update form
  const updateFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      title: selectedCert?.title || "",
      description: selectedCert?.description || "",
      icon: selectedCert?.icon || "",
      points: selectedCert?.points ?? 0,
      courseSlug: resolveCertCourseSlug(selectedCert, courseOptions),
      isComplianceCertificate: !!selectedCert?.isComplianceCertificate,
    },
    validationSchema: Yup.object({
      title: Yup.string().trim().required("Title is required"),
      description: Yup.string().trim(),
      icon: Yup.string()
        .trim()
        .max(4, "Icon must be maximum 4 characters")
        .required("Icon is required"),
      points: Yup.number()
        .typeError("Points must be a number")
        .integer("Points must be a whole number")
        .min(0, "Points cannot be negative")
        .required("Points is required"),
      courseSlug: Yup.string().trim(),
    }),
    onSubmit: (values) => {
      if (!selectedCert?.slug) return;

      const payload = {};

      const trimmedTitle = values.title.trim();
      const trimmedDescription = values.description.trim();

      if (trimmedTitle !== (selectedCert.title || "")) {
        payload.title = trimmedTitle;
      }

      if (trimmedDescription !== (selectedCert.description || "")) {
        payload.description = trimmedDescription;
      }

      const nextPoints = Number(values.points);
      if (nextPoints !== Number(selectedCert.points ?? 0)) {
        payload.points = nextPoints;
      }

      const prevCourseSlug = resolveCertCourseSlug(selectedCert, courseOptions);
      const nextCourseSlug = (values.courseSlug || "").trim();
      if (prevCourseSlug !== nextCourseSlug) {
        payload.courseSlug = nextCourseSlug || null;
      }

      const trimmedIcon = values.icon.trim();
      if (trimmedIcon !== (selectedCert.icon || "")) {
        payload.icon = trimmedIcon;
      }

      const nextIsCompliance = !!values.isComplianceCertificate;
      if (nextIsCompliance !== !!selectedCert.isComplianceCertificate) {
        payload.isComplianceCertificate = nextIsCompliance;
      }

      if (Object.keys(payload).length === 0) {
        toastr.info("No changes to update");
        return;
      }

      updateCertMutation.mutate(
        { slug: selectedCert.slug, payload },
        {
          onSuccess: () => {
            toastr.success("Certificate updated successfully!");
            setShowUpdateModal(false);
            setSelectedCert(null);
            updateFormik.resetForm();
            // Refresh the list
            mutate(buildListParams());
          },
          onError: () => {
            // Error toast is handled by global axios interceptor
          },
        }
      );
    },
  });

  // Handle edit button click
  const handleEdit = useCallback((cert) => {
    setSelectedCert(cert);
    setShowUpdateModal(true);
  }, []);

  const handleToggleStatus = useCallback(
    (cert, nextStatus) => {
      if (!cert?.slug) return;
      setPendingToggleSlug(cert.slug);
      toggleMutate(
        {
          slug: cert.slug,
          status: nextStatus,
        },
        {
          onSuccess: () => {
            toastr.success(
              `Certificate ${nextStatus ? "activated" : "deactivated"}`
            );
            setCertsState((prev) => ({
              ...prev,
              list: prev.list.map((item) =>
                item?.slug === cert.slug
                  ? { ...item, isActive: nextStatus }
                  : item
              ),
            }));
            mutate(buildListParams());
          },
          onSettled: () => {
            setPendingToggleSlug(null);
          },
        }
      );
    },
    [toggleMutate, mutate, buildListParams]
  );

  // Update formik values when selectedCert changes
  useEffect(() => {
    if (selectedCert) {
      updateFormik.setValues({
        title: selectedCert.title || "",
        description: selectedCert.description || "",
        icon: selectedCert.icon || "",
        points: selectedCert.points ?? 0,
        courseSlug: resolveCertCourseSlug(selectedCert, courseOptions),
        isComplianceCertificate: !!selectedCert.isComplianceCertificate,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCert, courseOptions]);

  const columns = useMemo(
    () => [
      {
        header: "Title",
        accessorKey: "title",
        enableColumnFilter: false,
        cell: (info) => info.getValue() || "-",
      },
      /* Legacy: type column — field removed from API
      {
        header: "Type",
        accessorKey: "type",
        enableColumnFilter: false,
        cell: (info) => {
          const type = info.getValue();
          if (!type) return "-";
          return (
            <Badge className="bg-primary" style={{ textTransform: "capitalize" }}>
              {type}
            </Badge>
          );
        }
      },
      */
      {
        header: "Points",
        accessorKey: "points",
        enableColumnFilter: false,
        cell: (info) => {
          const p = info.getValue();
          return p === undefined || p === null ? "-" : String(p);
        },
      },
      {
        header: "Course",
        id: "course",
        enableColumnFilter: false,
        cell: (info) => {
          const cert = info.row.original;
          if (cert?.course) {
            const text = cert.course?.title ?? cert.course?.slug;
            return (
              <span
                style={{ maxWidth: "220px", display: "inline-block" }}
                title={String(text)}
              >
                {text || "-"}
              </span>
            );
          }
        },
      },
      {
        header: "Description",
        accessorKey: "description",
        enableColumnFilter: false,
        cell: (info) => {
          const desc = info.getValue();
          if (!desc) return "-";
          return (
            <span
              style={{ maxWidth: "300px", display: "inline-block" }}
              title={desc}
            >
              {desc.length > 50 ? `${desc.substring(0, 50)}...` : desc}
            </span>
          );
        },
      },
      {
        header: "Has Service Number",
        accessorKey: "hasServiceNumber",
        enableColumnFilter: false,
        cell: (info) => {
          const hasServiceNumber = info.getValue();
          return hasServiceNumber ? (
            <Badge className="bg-success">Yes</Badge>
          ) : (
            <Badge className="bg-secondary">No</Badge>
          );
        },
      },
      {
        header: "Status",
        accessorKey: "isActive",
        enableColumnFilter: false,
        cell: (info) => {
          const isActive = !!info.getValue();
          const cert = info.row.original;
          const toggleId = `cert-status-${cert?.slug || info.row.id}`;
          const isRowPending =
            togglePending && pendingToggleSlug === cert?.slug;
          return (
            <div className="d-flex align-items-center gap-2">
              <div className="form-check form-switch mb-0">
                <Input
                  id={toggleId}
                  type="switch"
                  checked={isActive}
                  disabled={isRowPending}
                  onChange={() => handleToggleStatus(cert, !isActive)}
                />
                <Label className="visually-hidden" for={toggleId}>
                  Toggle certificate status
                </Label>
              </div>
              {isRowPending && (
                <i className="mdi mdi-loading mdi-spin text-muted" />
              )}
            </div>
          );
        },
      },
      {
        header: "Created Date",
        accessorKey: "createdAt",
        enableColumnFilter: false,
        cell: (info) =>
          info.getValue()
            ? new Date(info.getValue()).toLocaleDateString()
            : "-",
      },
      {
        header: "Action",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cellProps) => {
          const cert = cellProps.row.original;
          return (
            <Button
              color="primary"
              size="sm"
              onClick={() => handleEdit(cert)}
              disabled={updateCertMutation.isPending}
            >
              <i className="mdi mdi-pencil-outline me-1" />
              Edit
            </Button>
          );
        },
      },
    ],
    [
      handleEdit,
      handleToggleStatus,
      pendingToggleSlug,
      updateCertMutation.isPending,
      togglePending,
      courseIdToLabel,
    ]
  );

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs title="Certificates" breadcrumbItem="Certificates List" />

        <Row className="mb-3 g-2 align-items-end">
          <Col md={3}>
            <Label className="form-label">Search</Label>
            <Input
              type="text"
              placeholder="Search certificates..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
          </Col>
          <Col md={2}>
            <Label className="form-label">Active</Label>
            <Input
              type="select"
              value={isActiveFilter}
              onChange={(e) => {
                setIsActiveFilter(e.target.value);
                setPage(1);
              }}
            >
              {BOOL_FILTER_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Input>
          </Col>
          <Col md={2}>
            <Label className="form-label">Show DBS</Label>
            <Input
              type="select"
              value={showDBSFilter}
              onChange={(e) => {
                setShowDBSFilter(e.target.value);
                setPage(1);
              }}
            >
              {BOOL_FILTER_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Input>
          </Col>
          <Col md={2}>
            <Label className="form-label">Compliance Certificate</Label>
            <Input
              type="select"
              value={isComplianceFilter}
              onChange={(e) => {
                setIsComplianceFilter(e.target.value);
                setPage(1);
              }}
            >
              {BOOL_FILTER_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Input>
          </Col>
          <Col md="auto" className="ms-auto">
            <Button
              color="primary"
              onClick={() => setShowCreateModal(true)}
              disabled={loading}
            >
              <i className="mdi mdi-plus me-1" />
              Create Certificate
            </Button>
          </Col>
        </Row>

        <Row className="mb-3 g-2 align-items-center">
          <Col md="auto" className="ms-auto d-flex gap-2 align-items-center">
            <Button
              color="secondary"
              outline
              onClick={() => {
                setSearch("");
                setIsActiveFilter("all");
                setShowDBSFilter("all");
                setIsComplianceFilter("all");
                setLimit(10);
                setPage(1);
              }}
              disabled={loading}
            >
              Reset
            </Button>
            {/* Page size */}
            <Input
              type="select"
              value={limit}
              onChange={(e) => {
                setLimit(Number(e.target.value));
                setPage(1);
              }}
              style={{ width: "auto", minWidth: "100px" }}
            >
              {[10, 20].map((n) => (
                <option key={n} value={n}>
                  Show {n}
                </option>
              ))}
            </Input>
            <Button
              color="light"
              onClick={() => mutate(buildListParams())}
              disabled={loading}
            >
              <i className="mdi mdi-refresh" />
            </Button>
          </Col>
        </Row>

        <Card>
          <CardBody>
            {loading && certs.length === 0 ? (
              <Spinners setLoading={() => {}} />
            ) : error ? (
              <div className="text-danger">Failed to load certificates.</div>
            ) : (
              <>
                <TableContainer
                  columns={columns}
                  data={certs}
                  // server-side: disable client pagination & global filters
                  isPagination={false}
                  isCustomPageSize={false}
                  isGlobalFilter={false}
                  isJobListGlobalFilter={false}
                  tableClass="align-middle table-nowrap dt-responsive nowrap w-100 table-check dataTable no-footer dtr-inline mt-2 border-top"
                  theadClass="table-light"
                  divClassName="table-responsive"
                />

                <Paginations
                  total={total}
                  perPageData={limit}
                  currentPage={page}
                  setCurrentPage={setPage}
                  isShowingPageLength
                  paginationDiv="dataTables_paginate paging_simple_numbers pagination-rounded d-flex justify-content-end mt-3"
                  paginationClass="pagination"
                />
              </>
            )}
          </CardBody>
        </Card>

        {/* Create Certificate Modal */}
        <Modal
          isOpen={showCreateModal}
          toggle={() => setShowCreateModal(false)}
          centered
        >
          <ModalHeader toggle={() => setShowCreateModal(false)}>
            Create Certificate
          </ModalHeader>
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              createFormik.handleSubmit();
              return false;
            }}
          >
            <ModalBody>
              <div className="mb-3">
                <Label className="form-label">
                  Title <span className="text-danger">*</span>
                </Label>
                <Input
                  type="text"
                  name="title"
                  placeholder="Enter certificate title"
                  value={createFormik.values.title}
                  onChange={createFormik.handleChange}
                  onBlur={createFormik.handleBlur}
                  invalid={
                    createFormik.touched.title && !!createFormik.errors.title
                  }
                />
                {createFormik.touched.title && createFormik.errors.title && (
                  <FormFeedback type="invalid">
                    {createFormik.errors.title}
                  </FormFeedback>
                )}
              </div>

              <div className="mb-3">
                <Label className="form-label">Description (Optional)</Label>
                <Input
                  type="textarea"
                  name="description"
                  rows="3"
                  placeholder="Enter certificate description"
                  value={createFormik.values.description}
                  onChange={createFormik.handleChange}
                  onBlur={createFormik.handleBlur}
                />
              </div>

              <div className="mb-3">
                <Label className="form-label">
                  Points <span className="text-danger">*</span>
                </Label>
                <Input
                  type="number"
                  name="points"
                  min={0}
                  step={1}
                  max={5}
                  placeholder="e.g. 1"
                  value={createFormik.values.points}
                  onChange={createFormik.handleChange}
                  onBlur={createFormik.handleBlur}
                  invalid={
                    createFormik.touched.points && !!createFormik.errors.points
                  }
                />
                {createFormik.touched.points && createFormik.errors.points && (
                  <FormFeedback type="invalid">
                    {createFormik.errors.points}
                  </FormFeedback>
                )}
              </div>

              <div className="mb-3">
                <FormSelectField
                  inputId="cert-create-courseSlug"
                  label="Linked course (optional)"
                  placeholder="Select a course or leave empty"
                  options={courseSelectOptions}
                  isClearable
                  value={slugToSingleSelectValue(
                    createFormik.values.courseSlug,
                    courseSelectOptions
                  )}
                  onChange={(opt) =>
                    createFormik.setFieldValue("courseSlug", opt?.value ?? "")
                  }
                  onBlur={() =>
                    createFormik.setFieldTouched("courseSlug", true)
                  }
                  noOptionsMessage={() =>
                    courseSelectOptions.length
                      ? "No matching course"
                      : "No courses loaded yet"
                  }
                />
              </div>

              {/*
              Legacy: certificate type (gold/silver) removed from API — restore if backend adds it again.
              <div className="mb-3">
                <Label className="form-label">
                  Type <span className="text-danger">*</span>
                </Label>
                <Input type="select" name="type" value={createFormik.values.type} onChange={createFormik.handleChange} onBlur={createFormik.handleBlur}>
                  <option value="">Select Type</option>
                  {TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </Input>
              </div>
              */}

              <div className="mb-3">
                <Label className="form-label">
                  Icon <span className="text-danger">*</span>
                </Label>
                <Input
                  type="text"
                  name="icon"
                  placeholder="Enter icon (max 4 characters)"
                  value={createFormik.values.icon}
                  onChange={createFormik.handleChange}
                  onBlur={createFormik.handleBlur}
                  invalid={
                    createFormik.touched.icon && !!createFormik.errors.icon
                  }
                  maxLength={4}
                />
                {createFormik.touched.icon && createFormik.errors.icon && (
                  <FormFeedback type="invalid">
                    {createFormik.errors.icon}
                  </FormFeedback>
                )}
              </div>

              <div className="mb-0">
                <div className="form-check">
                  <Input
                    id="cert-create-isComplianceCertificate"
                    className="form-check-input"
                    type="checkbox"
                    name="isComplianceCertificate"
                    checked={!!createFormik.values.isComplianceCertificate}
                    onChange={createFormik.handleChange}
                    onBlur={createFormik.handleBlur}
                  />
                  <Label
                    className="form-check-label"
                    htmlFor="cert-create-isComplianceCertificate"
                  >
                    Compliance certificate
                  </Label>
                </div>
              </div>
            </ModalBody>
            <ModalFooter>
              <Button
                type="button"
                color="secondary"
                outline
                onClick={() => {
                  setShowCreateModal(false);
                  createFormik.resetForm();
                }}
                disabled={createCertMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                color="primary"
                disabled={createCertMutation.isPending || !createFormik.isValid}
              >
                {createCertMutation.isPending ? "Creating..." : "Create"}
              </Button>
            </ModalFooter>
          </Form>
        </Modal>

        {/* Update Certificate Modal */}
        <Modal
          isOpen={showUpdateModal}
          toggle={() => setShowUpdateModal(false)}
          centered
        >
          <ModalHeader toggle={() => setShowUpdateModal(false)}>
            Update Certificate
          </ModalHeader>
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              updateFormik.handleSubmit();
              return false;
            }}
          >
            <ModalBody>
              <div className="mb-3">
                <Label className="form-label">
                  Title <span className="text-danger">*</span>
                </Label>
                <Input
                  type="text"
                  name="title"
                  placeholder="Enter certificate title"
                  value={updateFormik.values.title}
                  onChange={updateFormik.handleChange}
                  onBlur={updateFormik.handleBlur}
                  invalid={
                    updateFormik.touched.title && !!updateFormik.errors.title
                  }
                />
                {updateFormik.touched.title && updateFormik.errors.title && (
                  <FormFeedback type="invalid">
                    {updateFormik.errors.title}
                  </FormFeedback>
                )}
              </div>

              <div className="mb-3">
                <Label className="form-label">Description (Optional)</Label>
                <Input
                  type="textarea"
                  name="description"
                  rows="3"
                  placeholder="Enter certificate description"
                  value={updateFormik.values.description}
                  onChange={updateFormik.handleChange}
                  onBlur={updateFormik.handleBlur}
                />
              </div>

              <div className="mb-3">
                <Label className="form-label">
                  Points <span className="text-danger">*</span>
                </Label>
                <Input
                  type="number"
                  name="points"
                  min={0}
                  step={1}
                  max={5}
                  placeholder="e.g. 1"
                  value={updateFormik.values.points}
                  onChange={updateFormik.handleChange}
                  onBlur={updateFormik.handleBlur}
                  invalid={
                    updateFormik.touched.points && !!updateFormik.errors.points
                  }
                />
                {updateFormik.touched.points && updateFormik.errors.points && (
                  <FormFeedback type="invalid">
                    {updateFormik.errors.points}
                  </FormFeedback>
                )}
              </div>
              <div className="mb-3">
                <FormSelectField
                  inputId="cert-update-courseSlug"
                  label="Linked course (optional)"
                  placeholder="Select a course or clear to unlink"
                  options={courseSelectOptions}
                  isClearable
                  value={slugToSingleSelectValue(
                    updateFormik.values.courseSlug,
                    courseSelectOptions
                  )}
                  onChange={(opt) =>
                    updateFormik.setFieldValue("courseSlug", opt?.value ?? "")
                  }
                  onBlur={() =>
                    updateFormik.setFieldTouched("courseSlug", true)
                  }
                  noOptionsMessage={() =>
                    courseSelectOptions.length
                      ? "No matching course"
                      : "No courses loaded yet"
                  }
                />
              </div>

              {/*
              Legacy: certificate type (gold/silver) removed from API — restore if backend adds it again.
              <div className="mb-3">
                <Label className="form-label">Type <span className="text-danger">*</span></Label>
                <Input type="select" name="type" value={updateFormik.values.type} onChange={updateFormik.handleChange} onBlur={updateFormik.handleBlur}>
                  <option value="">Select Type</option>
                  {TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </Input>
              </div>
              */}

              <div className="mb-3">
                <Label className="form-label">
                  Icon <span className="text-danger">*</span>
                </Label>
                <Input
                  type="text"
                  name="icon"
                  placeholder="Enter icon (max 4 characters)"
                  value={updateFormik.values.icon}
                  onChange={updateFormik.handleChange}
                  onBlur={updateFormik.handleBlur}
                  invalid={
                    updateFormik.touched.icon && !!updateFormik.errors.icon
                  }
                  maxLength={4}
                />
                {updateFormik.touched.icon && updateFormik.errors.icon && (
                  <FormFeedback type="invalid">
                    {updateFormik.errors.icon}
                  </FormFeedback>
                )}
              </div>

              <div className="mb-0">
                <div className="form-check">
                  <Input
                    id="cert-update-isComplianceCertificate"
                    className="form-check-input"
                    type="checkbox"
                    name="isComplianceCertificate"
                    checked={!!updateFormik.values.isComplianceCertificate}
                    onChange={updateFormik.handleChange}
                    onBlur={updateFormik.handleBlur}
                  />
                  <Label
                    className="form-check-label"
                    htmlFor="cert-update-isComplianceCertificate"
                  >
                    Compliance certificate
                  </Label>
                </div>
              </div>
            </ModalBody>
            <ModalFooter>
              <Button
                type="button"
                color="secondary"
                outline
                onClick={() => {
                  setShowUpdateModal(false);
                  setSelectedCert(null);
                  updateFormik.resetForm();
                }}
                disabled={updateCertMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                color="primary"
                disabled={updateCertMutation.isPending || !updateFormik.isValid}
              >
                {updateCertMutation.isPending ? "Updating..." : "Update"}
              </Button>
            </ModalFooter>
          </Form>
        </Modal>
      </div>
    </div>
  );
};

export default CertList;
